import { Component, Element, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../../common/colorable-component";
import { DropdownOptionSchemeDefinition, DropdownOptionSchemeName } from "./constants/dropdown-option-types";
import { DROPDOWN_OPTION_SCHEME_CONFIG, DROPDOWN_OPTION_SCHEME_MAP } from "./constants/dropdown-option-constants";
import pfx from "../../../utils/style-class-prefix-helper";
import { DropdownOptionSchemeConfiguration } from "../../../constants/colors";
import { DROPDOWN_SIZE_NORMAL } from "../constants/dropdown-constants";
import { DropdownSize } from "../constants/dropdown-types";
import { ICONSIZE_NORMAL } from "../../icon/constants/icon-constants";
import { CHECKBOX_STYLE_ALTERNATIVE } from "../../checkbox/constants/checkbox-constants";
import { BackgroundColor } from "../../background/constants/background-types";
import { StyledComponent } from "../../../decorators/styled-component";
// import { InitLogging } from "../../../decorators/init-logging";

@Component({
  tag: "eon-ui-dropdown-option",
  styleUrl: "dropdown-option.scss",
  shadow: true
})
export class DropdownOption
  implements ColorableComponent<DropdownOptionSchemeConfiguration<DropdownOptionSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: DropdownOptionSchemeConfiguration<DropdownOptionSchemeDefinition> = DROPDOWN_OPTION_SCHEME_CONFIG;
  schemeMap = DROPDOWN_OPTION_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: DropdownOptionSchemeDefinition;
  @State() onHover: boolean = false;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiDropdownOptionElement;

  /**Refers the Option Element*/
  option: HTMLElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   * Please do not use this Prop. It will be set internally by the E.ON UI Dropdown component.
   */
  @Prop({ reflect: true }) scheme: DropdownOptionSchemeName;

  /**
   * Reflects the native html value attribute. There is no Default Value.
   */
  @Prop({ reflect: true }) value: string;

  /**
   * If it's set, the label will appear on the right hand side of the Option value. The label is a string that gives a description on what the value of the option means.
   */
  @Prop({ reflect: true }) label: string;

  /**
   * If true this Option will be focused. Default is false.
   */
  @Prop({ mutable: true }) hasFocus: boolean = false;

  /**
   * Whether this is the currently selected Option. Default is false.
   */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * Whether the option has been selected by using a key
   */
  @Prop({ reflect: true }) isKeySelected: boolean = false;

  /**
   * Size of Dropdown element Normal and Small
   */
  @Prop() size: DropdownSize = DROPDOWN_SIZE_NORMAL;

  /**
   * Whether this option is disabled.
   */
  @Prop() disabled: boolean = false;

  /**
   * Multiselect dropdown functionality set by parent component. If active, checkboxes are added to the option so that multiple options can be selected.
   */
  @Prop() multiselect: boolean = false;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyFocus();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("hasFocus")
  @Watch("onHover")
  applyFocus() {
    if (this.hasFocus || this.onHover) {
      this.option.focus();
    }
  }

  syncSelectedPropAndCheckbox(event) {
    let checkbox = event.target as HTMLEonUiCheckboxElement;

    // if checkbox value is not equal the components checked state, then sync
    if (this.selected !== checkbox.checked) this.selected = checkbox.checked;
  }

  @Listen("mouseenter")
  onMouseEnter() {
    this.onHover = true;
  }

  @Listen("mouseleave")
  onMouseLeave() {
    this.onHover = false;
  }

  render() {
    return (
      <Host role="option" aria-selected={this.selected ? "true" : "false"} tabindex="-1">
        <div
          ref={(el) => (this.option = el as HTMLElement)}
          class={pfx({
            "dropdown-option": true,
            selected: this.selected,
            focus: this.hasFocus,
            "focus-visible": this.isKeySelected,
            multiselect: this.multiselect,
            [this.size]: this.size
          })}
        >
          {this.multiselect ? (
            <eon-ui-checkbox
              size="small"
              label={this.label}
              onValueChanged={(event) => this.syncSelectedPropAndCheckbox(event)}
              checked={this.selected}
              styleType={CHECKBOX_STYLE_ALTERNATIVE}
              forceFocusStyle={this.isKeySelected}
              forceHoverStyle={this.hasFocus || this.onHover}
            ></eon-ui-checkbox>
          ) : (
            <div
              class={pfx("label")}
              aria-label={
                this.selected
                  ? `${"\u2713"} 
 ${this.label}`
                  : this.label
              }
            >
              {this.label}
            </div>
          )}
          {!this.multiselect && (
            <eon-ui-icon
              class={pfx("icon")}
              name="radio_checkbox_tick"
              size={ICONSIZE_NORMAL}
              alignmentX="center"
              alignmentY="center"
            ></eon-ui-icon>
          )}
        </div>
      </Host>
    );
  }
}
